import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { authApi } from '../utils/api';

interface User {
  id: string;
  email: string;
  userType: string;
  role?: string;
  permissions?: string[];
  tenantId?: string;
  platformUserId?: string;
  requirePasswordReset?: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value);

  function loadAuthState() {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser);
      } catch {
        user.value = null;
      }
    }
  }

  function saveAuthState(userData: User) {
    localStorage.setItem('user', JSON.stringify(userData));
    user.value = userData;
  }

  function clearAuthState() {
    localStorage.removeItem('user');
    user.value = null;
    error.value = null;
  }

  async function login(email: string, password: string) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await authApi.login({ email, password });
      saveAuthState(response.user ?? response);
      return response.user ?? response;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Login failed. Please try again.';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    try {
      await authApi.logout();
    } catch {
      // ignore
    } finally {
      clearAuthState();
    }
  }

  async function refreshAccessToken() {
    try {
      await authApi.refreshToken();
    } catch (err) {
      clearAuthState();
      throw err;
    }
  }

  loadAuthState();

  return {
    user,
    isLoading,
    error,
    isAuthenticated,
    login,
    logout,
    refreshAccessToken,
    clearAuthState,
  };
});
